import React, { FormEvent, ReactNode, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import { CreateOutcome } from 'config/idempotentCreate';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import { useClassifiedErrorMessages } from 'reusable/useSaveError';
import { useRequiredFields } from 'reusable/useRequiredFields';
import { AccountRole, AccountStatus, Invitation } from './operatorApi';

/**
 * YAC-2026-009 PR5b。運営の画面の共通の部品。
 *
 * - ダイアログは既存の作り（useModalDialog・inModalLayer）に合わせる。body の直下に出し、背景は inert、
 *   フォーカスは中で巡回、Escape で閉じ、閉じたら開いたボタンへ戻す。
 * - 必須の欄は aria-required で伝え、送ったときに空なら知らせる（useRequiredFields）。
 * - 知らせの配色は、警告の配色（red-50・red-700。ログインの画面の誤りの表示と同じ）など、4.5 以上の組だけを使う。
 */

// #387 の Codex の指摘 4368575053。ページの戻り方は、既存の一覧（EV-460）と共通の reusable/pageRecovery に移した。
export { pageAfterRemoval, pageToRecover } from 'reusable/pageRecovery';

/** 失敗の文を決める場面。502 email_not_sent の言い方が変わる。 */
export type ErrorContext = 'create' | 'resend' | 'deactivate' | 'other';

const CODE_KEYS: Record<string, string> = {
  email_in_other_company: 'operator.error.email-in-other-company',
  email_is_operator: 'operator.error.email-is-operator',
  email_already_registered: 'operator.error.email-already-registered',
  already_manager: 'operator.error.already-manager',
  already_operator: 'operator.error.already-operator',
  last_operator: 'operator.error.last-operator',
  invitation_already_accepted: 'operator.error.invitation-already-accepted',
  account_has_other_roles: 'operator.error.account-has-other-roles',
};

/** 運営の API の失敗を、画面の言語の文にする。code の分かるものは運営の画面の文、ほかは共通の分類。 */
export function useOperatorErrors() {
  const { t } = useTranslation('layout');
  const classified = useClassifiedErrorMessages();
  return useCallback(
    (error: unknown, context: ErrorContext = 'other'): string[] => {
      const response = (error as { response?: { status?: number; data?: { code?: unknown } } })?.response;
      const code = typeof response?.data?.code === 'string' ? response.data.code : null;
      if (response?.status === 502 && code === 'email_not_sent') {
        return [t(context === 'resend' ? 'operator.error.email-not-sent-resend' : 'operator.error.email-not-sent')];
      }
      if (code === 'last_operator' && context === 'deactivate') {
        return [t('operator.error.last-operator-deactivate')];
      }
      if (code && CODE_KEYS[code]) return [t(CODE_KEYS[code])];
      if (response?.status === 404) return [t('operator.error.not-found')];
      return classified(error);
    },
    [t, classified]
  );
}

// #387 の Codex の指摘 4368590863。結果が分からないままか（入力を固定するか）は、既存の登録の画面（EV-461）と
// 共通の config/idempotentCreate の isUnresolvedCreate に移した。
export { isUnresolvedCreate as isUnresolved } from 'config/idempotentCreate';

/** 作成・招待の結果のうち、作れなかったものを文にする（created は null）。 */
export function useOutcomeMessages() {
  const { t } = useTranslation('layout');
  const errors = useOperatorErrors();
  return useCallback(
    (outcome: CreateOutcome): string[] | null => {
      if (outcome.status === 'created') return null;
      if (outcome.status === 'expired') return [t('operator.error.retry-expired')];
      if (outcome.status === 'unknown') return [t('operator.error.outcome-unknown')];
      return errors(outcome.error, 'create');
    },
    [t, errors]
  );
}

/** 失敗の知らせ（role=alert）。 */
export const ErrorNotice: React.FC<{ messages: string[]; id?: string }> = ({ messages, id }) =>
  messages.length > 0 ? (
    <div id={id} role="alert" className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-body-2 text-red-700">
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      <div className="flex flex-col gap-1">
        {messages.map((message, index) => (
          <p key={index}>{message}</p>
        ))}
      </div>
    </div>
  ) : null;

/** 成功の知らせ（role=status）。 */
export const SuccessNotice: React.FC<{ message: string | null; onClose?: () => void }> = ({ message, onClose }) => {
  const { t } = useTranslation('common');
  return (
    <div role="status" aria-live="polite">
      {message ? (
        <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-body-2 text-green-800">
          <p className="flex-1">{message}</p>
          {onClose ? (
            <button type="button" className="underline" onClick={onClose}>
              {t('ui.close')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const STATUS_CLASS: Record<AccountStatus, string> = {
  active: 'bg-green-50 text-green-800 border-green-200',
  invited: 'bg-amber-50 text-amber-800 border-amber-200',
  invitation_expired: 'bg-red-50 text-red-700 border-red-200',
  inactive: 'bg-gray-100 text-gray-700 border-gray-300',
};

/** アカウントの状態（招待中・有効・無効・招待の期限切れ）。 */
export const StatusBadge: React.FC<{ status: AccountStatus }> = ({ status }) => {
  const { t } = useTranslation('layout');
  return (
    <span className={`inline-block whitespace-nowrap rounded border px-2 py-0.5 text-caption ${STATUS_CLASS[status] ?? STATUS_CLASS.inactive}`}>
      {t(`operator.account.status-${status}`)}
    </span>
  );
};

/** 役割の並び（管理者・運営 など）。 */
export function useRoleLabel() {
  const { t } = useTranslation('layout');
  return useCallback(
    (roles: AccountRole[]) =>
      roles
        .filter((role) => role === 'driver' || role === 'manager' || role === 'operator')
        .map((role) => t(`operator.account.role-${role}`))
        .join('・'),
    [t]
  );
}

interface DialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** 送信中は閉じない（Escape・キャンセルも効かない）。 */
  busy?: boolean;
  /** 注意を強く出すダイアログ（自分自身を外すなど）。見出しの前に警告のアイコンを出す。 */
  danger?: boolean;
}

/** 運営の画面のモーダルのダイアログの枠（見出しと中身）。開いている間だけ描く。 */
export const OperatorDialog: React.FC<DialogProps> = ({ title, onClose, children, busy = false, danger = false }) => {
  const titleId = useId();
  const close = () => {
    if (!busy) onClose();
  };
  const ref = useModalDialog(true, close);
  return inModalLayer(
    <div className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-gray-600 bg-opacity-50 p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`flex w-full max-w-lg flex-col gap-4 rounded-lg bg-white p-6 shadow-xl ${danger ? 'border-t-4 border-red-700' : ''}`}
      >
        <h2 id={titleId} className="flex items-center gap-2 text-subtitle-1 font-bold text-neutral/300">
          {danger ? <ExclamationTriangleIcon className="h-6 w-6 text-red-700" aria-hidden="true" /> : null}
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
};

const buttonBase = 'inline-flex h-10 items-center justify-center rounded-md px-4 text-subtitle-2 disabled:opacity-60';
export const primaryButton = `${buttonBase} bg-primary/300 text-white`;
export const secondaryButton = `${buttonBase} border border-neutral/100 bg-white text-neutral/300`;
export const dangerButton = `${buttonBase} bg-red-700 text-white`;
export const inputClass =
  'h-10 w-full rounded-md border border-neutral/100 px-3 text-body-2 text-neutral/300 focus:border-primary/300 focus:outline-none';

interface ConfirmDialogProps {
  title: string;
  /** 確かめる文（1つ以上）。 */
  body: ReactNode;
  confirmLabel: string;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
  busy: boolean;
  errors: string[];
  danger?: boolean;
}

/** 操作の前の確認。既定のフォーカスは「キャンセル」（最初のボタン）。 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
  busy,
  errors,
  danger = false,
}) => {
  const { t } = useTranslation('common');
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy) onConfirm();
  };
  return (
    <OperatorDialog title={title} onClose={onClose} busy={busy} danger={danger}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 text-body-2 text-neutral/300">{body}</div>
        <ErrorNotice messages={errors} />
        <div className="grid grid-cols-2 gap-4">
          <button type="button" className={secondaryButton} onClick={onClose} disabled={busy}>
            {t('ui.cancel')}
          </button>
          <button type="submit" className={danger ? dangerButton : primaryButton} disabled={busy}>
            {confirmLabel}
          </button>
        </div>
      </form>
    </OperatorDialog>
  );
};

/** 招待を送った結果（成功なら null。ダイアログは呼び出し側が閉じる）。 */
export type InviteFailure = { messages: string[]; unresolved: boolean } | null;

interface InviteDialogProps {
  title: string;
  lead: string;
  submitLabel: string;
  /** 招待を送る。失敗なら文と、結果が分からないまま（isUnresolved）かを返す。 */
  onSubmit: (invitation: Invitation) => Promise<InviteFailure>;
  onClose: () => void;
}

/** メールアドレスと名前で招待する（管理者の招待・運営の招待）。 */
export const InviteDialog: React.FC<InviteDialogProps> = ({ title, lead, submitLabel, onSubmit, onClose }) => {
  const { t } = useTranslation('layout');
  const emailId = useId();
  const nameId = useId();
  const errorId = useId();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  // 結果が分からないまま。入力を送った値で固定し、同じ内容で送り直すだけにする（isUnresolved）。
  const [unresolved, setUnresolved] = useState(false);
  const required = useRequiredFields({ email: emailId, name: nameId });
  const locked = busy || unresolved;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const values = { email: email.trim(), name: name.trim() };
    if (!required.check(values)) return;
    // 送る値を欄にも出す（前後の空白を除いた値。固定したときに、送った内容と同じに見えるように）。
    setEmail(values.email);
    setName(values.name);
    setBusy(true);
    setErrors([]);
    const failed = await onSubmit(values);
    setBusy(false);
    setUnresolved(failed?.unresolved ?? false);
    if (failed) setErrors(failed.messages);
  };

  return (
    <OperatorDialog title={title} onClose={onClose} busy={busy}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <p className="text-body-2 text-neutral/300">{lead}</p>
        <div className="flex flex-col gap-1">
          <label htmlFor={emailId} className="text-subtitle-2 text-neutral/300">
            {t('operator.account.field-email')}
          </label>
          <input
            id={emailId}
            type="email"
            autoComplete="off"
            className={inputClass}
            value={email}
            disabled={locked}
            onChange={(event) => {
              setEmail(event.target.value);
              required.clear('email');
            }}
            {...required.fieldProps('email', { describedBy: errors.length > 0 ? errorId : undefined })}
          />
          {required.message('email')}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={nameId} className="text-subtitle-2 text-neutral/300">
            {t('operator.account.field-name')}
          </label>
          <input
            id={nameId}
            type="text"
            autoComplete="off"
            className={inputClass}
            value={name}
            disabled={locked}
            onChange={(event) => {
              setName(event.target.value);
              required.clear('name');
            }}
            {...required.fieldProps('name')}
          />
          {required.message('name')}
        </div>
        <ErrorNotice messages={errors} id={errorId} />
        {unresolved ? <p className="text-body-2 text-neutral/300">{t('operator.unresolved-note')}</p> : null}
        <div className="grid grid-cols-2 gap-4">
          <button type="button" className={secondaryButton} onClick={onClose} disabled={busy}>
            {t('ui.cancel', { ns: 'common' })}
          </button>
          <button type="submit" className={primaryButton} disabled={busy}>
            {busy ? t('operator.sending') : unresolved ? t('operator.resend-same') : submitLabel}
          </button>
        </div>
      </form>
    </OperatorDialog>
  );
};
